import React, { useContext, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Avatar,
	Button,
	Chip,
	Drawer,
	IconButton,
	InputLabel,
	Link,
	Modal,
	Paper,
	Tooltip,
	Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import KeyboardArrowRightOutlinedIcon from '@material-ui/icons/KeyboardArrowRightOutlined';
import RestoreOutlinedIcon from "@material-ui/icons/RestoreOutlined";
import Viewer from 'react-viewer';

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import BezinhoImg from '../../assets/miscSvg/bezinho.svg';
import ConfirmationModal from "../ConfirmationModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import GroupParticipantsModal from "../GroupParticipantsModal";
import InternalGroupEditionModal from "../InternalGroupEditionModal";
import InternalChatImg from "../../assets/miscPng/internalChat.png";
import InternalGroupParticipantsModal from "../InternalGroupParticipantsModal";
import InternalGroupImg from "../../assets/miscPng/internalGroup.png";
import MarkdownWrapper from "../MarkdownWrapper";
import MediasGalleryModal from "../MediasGalleryModal";
import toastError from "../../errors/toastError";
import truncateString from '../../utils/truncateString';
import WhatsappOfficialImg from "../../assets/miscJpg/whatsappOfficial.jpg";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import JidUtils from "../../utils/classes/JidUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	lidChip: {
		margin: 2,
		border: `2px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,
	},

	closeIcon: { color: theme.palette.text.primary, },

	accordionSummary: { "& .MuiAccordionSummary-expandIcon": { color: theme.palette.text.primary }, },

	accordionSummaryMainContainer: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		alignItems: "center",
		justifyContent: "center",
		gap: "5px",
		cursor: "default",
	},

	showMediasButton: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		alignItems: "center",
		justifyContent: "center",
		gap: "0px",
		cursor: "pointer",
	},

	drawer: { width: drawerWidth, flexShrink: 0, },

	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},

	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.default,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},

	content: {
		display: "flex",
		backgroundColor: theme.palette.background.paper,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactNumber: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		flexWrap: "wrap",
		gap: "3px",
	},

	contactNumberLidField: {
		cursor: "default",
		"&:hover": { textDecoration: "none" }
	},

	contactAvatar: { margin: 15, width: 160, height: 160, cursor: "pointer", },

	contactAvatarModal: {
		margin: 15,
		cursor: "pointer",
		width: 800,
		height: 800,
		[theme.breakpoints.down("sm")]: { width: 350, height: 350, },
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": { margin: 4, },
	},

	contactDetails: { marginTop: 8, padding: 8, display: "flex", flexDirection: "column", },

	contactExtraInfo: { marginTop: 4, padding: 6, },

	floatingButton: {
		transition: 'all 0.3s ease',
		'&:hover': { transform: 'translateY(-5px)', color: theme.palette.primary.main, },
	},

	imgModal: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		backdropFilter: 'blur(5px)',
		animation: '$fadeIn 0.7s ease',
	},

	'@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(-50%)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },

	imgContainer: { borderRadius: "50%", width: "250px", },

	label: {
		margin: 2,
		border: `2px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,
		padding: "7px",
		borderRadius: "5px"
	},

	lastThreeImagesContainer: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		jsutifyContent: "space-around",
		gap: "1em",
	},

	lastImage: {
		borderRadius: "5px",
		width: "75px",
		height: "75px",
		cursor: "pointer",
		transition: "all 0.3s ease",
		userDrag: "none",

		"&:hover": { opacity: "0.9", transform: "translateY(-5px)", },
	},

	customBadge: {
		border: `1px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,
	
		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		fontSize: "10px",
		width: "fit-content",
		display: "flex",
		gap: "3px",
		alignItems: "center",
	},

	contactNameContainer: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		alignItems: "center",
	},
}));

const ContactDrawer = ({
	open,
	handleDrawerClose,
	contact,
	ticket,
	loading,
	setIsContactModalOpen,
	setContactModalOpen,
	handleOpenContactModal
}) => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);

	const timeoutCopyRef = useRef(null);

	const [groupParticipantsModalOpen, setGroupParticpantsModalOpen] = useState(false);
	const [selectedGroupTicket, setSelectedGroupTicket] = useState(null);

	const [connectionAccordionOpen, setConnectionAccordionOpen] = useState(true);
	const [labelsAccordionOpen, setLabelsAccordionOpen] = useState(true);
	const [openModalImg, setOpenModalImg] = useState(false);

	const [isInternalGroupEditionModalOpen, setIsInternalGroupEditionModalOpen] = useState(false);
	const [isInternalGroupParticipantsModalOpen, setIsInternalGroupParticipantsModalOpen] = useState(false);
	const [closeInternalGroupConfirmationModal, setCloseInternalGroupConfirmationModal] = useState(false);
	const [leaveInternalGroupConfirmationModal, setLeaveInternalGroupConfirmationModal] = useState(false);

	const [mediasAccordionOpen, setMediasAccordionOpen] = useState(true);
	const [mediasCount, setMediasCount] = useState(0);
	const [lastThreeImages, setLastThreeImages] = useState([]);
	const [mediasGalleryModalOpen, setMediasGalleryModalOpen] = useState(false);

	const [viewerVisible, setViewerVisible] = useState(false);
	const [viewerImageUrl, setViewerImageUrl] = useState("");

	const [copyInfoTooltip, setCopyInfoTooltip] = useState("contactDrawer.tooltips.copy");

	const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		if (!open || ticket.type !== 0) return;

		const delayDebounceFN = setTimeout(() => {
			const fetchLastThreeImages = async () => {
				const { data: lastThreeImages } = await api.get(`/lastThreeImages/${ticket.contactId}`);
				setLastThreeImages(lastThreeImages);

				const { data: mediasCount } = await api.get(`/mediasCount/${ticket.contactId}`);
				setMediasCount(mediasCount);
			};

			fetchLastThreeImages();
		}, 500);

		return () => clearTimeout(delayDebounceFN);
	}, [open, ticket]);



	// 	***************
	// 	** Functions **
	// 	***************
  const handleClose = () => {
    setOpenModalImg(false);
		setViewerVisible(false);
		setViewerImageUrl("");
  };

	const handleAvatarClick = () => {
    setOpenModalImg(true);
  };

	const handleOpenGroupParticipantsModal = (groupTicket) => {
		setSelectedGroupTicket(groupTicket);
		setGroupParticpantsModalOpen(true);
	};

	const handleCloseGroupParticipantsModal = () => {
		setGroupParticpantsModalOpen(false);
		setSelectedGroupTicket(null);
	};
	
	const handleCopyInfo = async (info, isPhoneNumber, isTicketGroup) => {
		if (!info) return;

		setCopyInfoTooltip("contactDrawer.tooltips.copied");

		const formattedInfo = isPhoneNumber ? JidUtils.formatJidToClipboard(info, isTicketGroup) : info;
		await navigator.clipboard.writeText(formattedInfo);

		if (timeoutCopyRef.current) clearTimeout(timeoutCopyRef.current);
		timeoutCopyRef.current = setTimeout(() => setCopyInfoTooltip("contactDrawer.tooltips.copy"), 500);
	};

	const handleViewerImage = (imageUrl) => {
		setViewerVisible(true);
		setViewerImageUrl(imageUrl);
	};

	const resetViewerImage = () => {
		setViewerVisible(false);
		setViewerImageUrl("");
	};

	const renderLastThreeImages = () => {
		return (
			lastThreeImages.map(image => (
				<Tooltip title={image.mediaUrl.split("/").pop()} placement="top-start" arrow>
					<img
						onClick={() => handleViewerImage(image.mediaUrl)}
						className={classes.lastImage}
						src={image.mediaUrl}
						alt=""
					/>
				</Tooltip>
			))
		)
	};

	const handleOpenMediasGalleryModal = (event) => {
		event.stopPropagation();
		setMediasGalleryModalOpen(true);
	};
	
	const handleCloseMediasGalleryModal = () => {
		setMediasGalleryModalOpen(false);
	};

	const renderContactDrawerInfo = (ticketType) => {
		let avatarSource = "";
		if (ticketType === 0 && contact?.name === DefaultValuesUtils.SPECIAL_NAMES.whatsapp) avatarSource = WhatsappOfficialImg;
		else if ([0, 1].includes(ticketType)) avatarSource = contact?.profilePicUrl;
		else if (ticketType === 2) avatarSource = BezinhoImg;
		else if (ticketType === 3) avatarSource = InternalChatImg;
		else avatarSource = InternalGroupImg;

		return (
			<>
				<Avatar src={avatarSource} className={classes.contactAvatar} onClick={handleAvatarClick} />

				{ticket.type === 0 && ticket.contact?.temporaryChatSetting?.isEnabled && (
					<Tooltip
						style={{
							position: "absolute",
							fontSize: "2.50em",
							left: "90px",
							top: "225px",
						}}
						title={i18n.t("tickets.notification.temporaryChat").replace("@", ticket.contact?.temporaryChatSetting?.duration)}
						placement="bottom-start"
						PopperProps={{ disablePortal: true }}
						arrow
					>
						<RestoreOutlinedIcon className={classes.simpleIcon} />
					</Tooltip>
				)}

				<Modal className={classes.imgModal} open={openModalImg} onClose={handleClose}>
					<Avatar src={avatarSource} className={classes.contactAvatarModal} onClick={handleClose} />
				</Modal>
			</>
		);
	};

	const handleCloseInternalGroup = async () => {
		try {
			await api.patch(`/internalGroups/close/${ticket.id}`);
			DefaultFunctionsUtils.redirectToTicketsPage(history);
			setShouldSyncRouteOnMenu(true);
		}
		catch (exception) {
			toastError(exception);
			console.log("handleCloseInternalGroup exception:", exception);
		}
	};

	const handleLeaveInternalGroup = async () => {
		try {
			await api.delete(`/ticketUsers/leave/${ticket.id}`);
			DefaultFunctionsUtils.redirectToTicketsPage(history);
			setShouldSyncRouteOnMenu(true);
		}
		catch (exception) {
			toastError(exception);
			console.log("handleLeaveInternalGroup exception:", exception);
		}
	};

	const handleRenderJid = (contact) => {
		const phoneNumber = contact.number;
		const lid = contact.lid;

		return (
			<>
				{phoneNumber && (
					<>
						<Link href={`tel:${phoneNumber.replace(/[^\d]/g, "")}`}>{phoneNumber}</Link>

						<Tooltip title={i18n.t(copyInfoTooltip)} placement="top-start" arrow>
							<IconButton
								color="inherit"
								className={classes.floatingButton}
								onClick={async () => await handleCopyInfo(phoneNumber, true, contact.isGroup)}
							>
								<FileCopyOutlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</>
				)}

				{lid && (
					<>
						<Link className={classes.contactNumberLidField}>{JidUtils.formatJid(lid, false, true)}</Link>

						<Tooltip title={i18n.t(copyInfoTooltip)} placement="top-start" arrow>
							<IconButton
								color="inherit"
								className={classes.floatingButton}
								onClick={async () => await handleCopyInfo(contact.lid, false, contact.isGroup)}
							>
								<FileCopyOutlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</>
				)}
			</>
		);
	};

	const renderContactPreference = (whatsappId, contactPreferences) => {
		const contactPreference = contactPreferences.find(preference => preference.whatsappId === whatsappId)?.preference;
		
		if (!contactPreference || contactPreference === 'resume') {
			return (<></>);
		}

		return (
			<Chip variant="outlined" label={i18n.t('tickets.notification.contactPreference.stop')} className={classes.lidChip} />
		);
	};

	const renderContactName = (ticket) => {
		const isAddressingModeLid = ticket.contact?.addressingMode?.addressingMode === DefaultValuesUtils.ADDRESSING_MODES.lid;
		const contactName = truncateString(ticket.contact?.name, 27);
		const shouldRenderContactPreference = ticket.whatsapp?.isMetaApi && ticket.contact?.contactPreferences?.length > 0;

		return (
			<>
				<div className={classes.contactNameContainer}>
					<Typography>{contactName}</Typography>

					<Tooltip title={i18n.t(copyInfoTooltip)} placement="top-start" arrow>	
						<IconButton
							color="inherit"
							className={classes.floatingButton}
							onClick={async () => await handleCopyInfo(ticket.contact?.name, false, contact.isGroup)}
						>
							<FileCopyOutlinedIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</div>
					
				{isAddressingModeLid && (
					<Chip variant="outlined" label={i18n.t("tickets.notification.lid")} className={classes.lidChip} />
				)}

				{shouldRenderContactPreference && (
					renderContactPreference(ticket.whatsappId, ticket.contact.contactPreferences)
				)}
			</>
		);
	};



	// 	************
	// 	** Return **
	// 	************
	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{ container: document.getElementById("drawer-container"), style: { position: "absolute" }, }}
			classes={{ paper: classes.drawerPaper, }}
		>

			{ticket.status === "group" && ticket.type === 0 && (
				<GroupParticipantsModal
					open={groupParticipantsModalOpen}
					onClose={handleCloseGroupParticipantsModal}
					ticketId={selectedGroupTicket}
				/>
			)}

			{ticket.type === 4 && (
				<>
					<InternalGroupEditionModal
						open={isInternalGroupEditionModalOpen}
						onClose={() => {
							setContactModalOpen(false);
							setIsInternalGroupEditionModalOpen(false);
						}}
						ticketId={ticket.id}
						ticketName={ticket.noteName}
					/>

					<InternalGroupParticipantsModal
						open={isInternalGroupParticipantsModalOpen}
						onClose={() => setIsInternalGroupParticipantsModalOpen(false)}
						ticketId={ticket.id}
					/>

					<ConfirmationModal
						title={`${i18n.t("contactDrawer.confirmationModals.closeInternalGroupTitle")} ${ticket.noteName}?`}
						open={closeInternalGroupConfirmationModal}
						onClose={() => setCloseInternalGroupConfirmationModal(false)}
						onConfirm={async () => await handleCloseInternalGroup()}
					/>

					<ConfirmationModal
						title={`${i18n.t("contactDrawer.confirmationModals.leaveInternalGroupTitle")} ${ticket.noteName}?`}
						open={leaveInternalGroupConfirmationModal}
						onClose={() => setLeaveInternalGroupConfirmationModal(false)}
						onConfirm={handleLeaveInternalGroup}
					/>
				</>
			)}

			<Viewer
				downloadable={true}
				downloadInNewWindow={true}
				images={[{ src: viewerImageUrl, downloadUrl: viewerImageUrl }]}
				onClose={resetViewerImage}
				onMaskClick={resetViewerImage}
				visible={viewerVisible}
				zIndex={10000}
			/>

			<MediasGalleryModal
				open={mediasGalleryModalOpen}
				onClose={handleCloseMediasGalleryModal}
				contactId={ticket.contactId}
			/>

			<div className={classes.header}>
				<IconButton className={classes.closeIcon} onClick={handleDrawerClose}><CloseIcon /></IconButton>
				<Typography style={{ justifySelf: "center" }}>{i18n.t("contactDrawer.header")}</Typography>
			</div>

			{loading 
				? (
					<ContactDrawerSkeleton classes={classes} />
				)
				: (
					<div className={classes.content}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							{renderContactDrawerInfo(ticket.type)}
	
							{ticket.type === 0 && ![DefaultValuesUtils.SPECIAL_NAMES.whatsapp, DefaultValuesUtils.SPECIAL_NAMES.metaAi].includes(contact?.name) && (
								<>
									{renderContactName(ticket)}
									<Typography className={classes.contactNumber}>{handleRenderJid(contact)}</Typography>

									<Button
										variant="outlined"
										color="inherit"
										className={classes.floatingButton}
										onClick={handleOpenContactModal}
									>
										{ticket.status === "group" ? i18n.t("contactDrawer.buttons.editGroup") : i18n.t("contactDrawer.buttons.edit")}
									</Button>

									{ticket.status === "group" && (
										<Button
											variant="outlined"
											color="inherit"
											className={classes.floatingButton}
											onClick={() => handleOpenGroupParticipantsModal(ticket.id)}
										>
											{i18n.t("contactDrawer.buttons.participants")}
										</Button>
									)}
								</>
							)}

							{ticket.type === 0 && [DefaultValuesUtils.SPECIAL_NAMES.whatsapp, DefaultValuesUtils.SPECIAL_NAMES.metaAi].includes(contact?.name) && (
								<Typography>{truncateString(contact?.name, 27)}</Typography>
							)}

							{(ticket.type === 1 || ticket.type === 2) && (
								<Typography>{truncateString(ticket.noteName, 27)}</Typography>
							)}

							{ticket.type === 3 && (
								<>
									<Typography>{truncateString(ticket.noteName, 27)}</Typography>
									<span className={classes.customBadge}>{ticket.user?.name} || {ticket.userInternalChat?.name}</span>
								</>
							)}

							{ticket.type === 4 && (
								<>
									<Typography>{truncateString(ticket.noteName, 27)}</Typography>

									<Button
										variant="outlined"
										color="inherit"
										className={classes.floatingButton}
										onClick={() => {
											setIsInternalGroupEditionModalOpen(true);
											setIsContactModalOpen(true);
										}}
									>
										{i18n.t("contactDrawer.buttons.editGroup")}
									</Button>

									<Button
										variant="outlined"
										color="inherit"
										className={classes.floatingButton}
										onClick={() => setIsInternalGroupParticipantsModalOpen(true)}
									>
										{i18n.t("contactDrawer.buttons.participants")}
									</Button>

									{ticket.ticketUsers.some(
										ticketUser => ticketUser.userId === user.id && ticketUser.userProfile === "superadmin"
									) && (
										<Button
											variant="outlined"
											color="inherit"
											className={classes.floatingButton}
											onClick={() => setCloseInternalGroupConfirmationModal(true)}
										>
											{i18n.t("contactDrawer.buttons.closeGroup")}
										</Button>
									)}

									{ticket.ticketUsers.some(
										ticketUser => ticketUser.userId === user.id && ticketUser.userProfile !== "superadmin"
									) && (
										<Button
											variant="outlined"
											color="inherit"
											className={classes.floatingButton}
											onClick={() => setLeaveInternalGroupConfirmationModal(true)}
										>
											{i18n.t("contactDrawer.buttons.leaveGroup")}
										</Button>
									)}
								</>
							)}
						</Paper>

						{ticket.type === 0 && (
							<>
								{!ticket.isGroup && ticket.type === 0 && (
									<Accordion expanded={mediasAccordionOpen}>
										<AccordionSummary
											id="medias-accordion-header"
											aria-controls="medias-accordion-header"
											expandIcon={<ExpandMoreIcon />}
											TransitionProps={{ unmountOnExit: true }}
											className={classes.accordionSummary}
											onClick={() => setMediasAccordionOpen((prevValue) => !prevValue )}
										>
											<div className={classes.accordionSummaryMainContainer}>
												<Typography className={classes.accordionHeading}>
													{i18n.t("contacts.accordion.title3")}
												</Typography>

												<Tooltip title={i18n.t("contacts.accordion.showAllMedias")} placement="top-start" arrow>
													<div
														className={`${classes.floatingButton} ${classes.showMediasButton}`} 
														onClick={(event) => handleOpenMediasGalleryModal(event)}
													>
														<span>{mediasCount}</span>
														<KeyboardArrowRightOutlinedIcon color="inherit" />
													</div>
												</Tooltip>
											</div>
										</AccordionSummary>

										{lastThreeImages.length > 0 && (
											<AccordionDetails className={classes.contactLabelsField}>
												<div className={classes.lastThreeImagesContainer}>{renderLastThreeImages()}</div>
											</AccordionDetails>
										)}
									</Accordion>
								)}

								<Accordion className={classes.accordionSummary} expanded={connectionAccordionOpen}>
									<AccordionSummary
										id="connection-accordion-header"
										aria-controls="connections-accordion-header"
										expandIcon={<ExpandMoreIcon />}
										TransitionProps={{ unmountOnExit: true }}
										onClick={() => setConnectionAccordionOpen((prevValue) => !prevValue )}
									>
										<Typography className={classes.accordionHeading}>
											{i18n.t("contacts.accordion.title2")}
										</Typography>
									</AccordionSummary>
									<AccordionDetails className={classes.contactLabelsField}>
										<span className={classes.label}>{contact.whatsapp ? contact.whatsapp.name : "-"}</span>
									</AccordionDetails>
								</Accordion>

								{!ticket.isGroup && (
									<Accordion className={classes.accordionSummary} expanded={labelsAccordionOpen}>
										<AccordionSummary
											id="labels-accordion-header"
											aria-controls="labels-accordion-header"
											expandIcon={<ExpandMoreIcon />}
											TransitionProps={{ unmountOnExit: true }}
											onClick={() => setLabelsAccordionOpen((prevValue) => !prevValue )}
										>
											<Typography className={classes.accordionHeading}>
												{i18n.t("contacts.accordion.title")}
											</Typography>
										</AccordionSummary>

										<AccordionDetails className={classes.contactLabelsField}>
											{/* Contact has labels */}
											{contact.labels && contact.labels.map((label) => (
												<span style={{ border: `2px solid ${label.color}`, padding: "7px", borderRadius: "20px" }}>
													{label?.name}
												</span>
											))}

											{/* Contact does not have labels */}
											{(
												(contact.labels && contact.labels.length === 0)
												|| (contact.labels === null || contact.labels === undefined)
											) && (
												<Typography>
													{i18n.t("contacts.accordion.noLabels")}
												</Typography>)
											}
										</AccordionDetails>
									</Accordion>
								)}
								
								<Paper square variant="outlined" className={classes.contactDetails}>
									<Typography variant="subtitle1">{i18n.t("contactDrawer.extraInfo")}</Typography>

									{contact?.extraInfo?.map(info => (
										<Paper
											key={info.id}
											square
											variant="outlined"
											className={classes.contactExtraInfo}
										>
											<InputLabel>{info?.name}</InputLabel>
											<Typography component="div" noWrap style={{ paddingTop: 2 }}>
												<MarkdownWrapper>{info.value}</MarkdownWrapper>
											</Typography>
										</Paper>
									))}
								</Paper>
							</>
						)}
					</div>
				)
			}
		</Drawer>
	);
};

export default ContactDrawer;
